<template>
    <modal :open="show" size="screen" padding="px-0" @close="$emit('closeModal')">
        <div class="flex flex-col max-h-[85vh]">
            <div class="flex items-center justify-between px-6 pb-3 border-b border-black/10 dark:border-gray-600">
                <div>
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
                        Histórico de Webhooks
                    </h2>
                    <p class="text-xs text-gray-600 dark:text-gray-300">
                        Transação #{{ transaction.id }} — Hash {{ transaction.hash }}
                    </p>
                </div>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        class="flex items-center gap-1 rounded-md border border-indigo-500/50 px-2 py-1 text-xs text-indigo-300 hover:border-indigo-400 hover:text-indigo-200"
                        @click="showHelp = !showHelp"
                    >
                        <QuestionMarkCircleIcon class="h-4 w-4" />
                        Ajuda
                    </button>
                    <button
                        class="text-gray-600 dark:text-gray-300 hover:text-white"
                        @click="$emit('closeModal')"
                    >
                        <XMarkIcon class="h-6 w-6" />
                    </button>
                </div>
            </div>

            <div
                v-if="showHelp"
                class="mx-6 mt-3 rounded-lg border border-indigo-500/40 bg-indigo-950/50 p-4 text-xs text-gray-200"
            >
                <div class="flex items-start justify-between gap-4">
                    <div class="space-y-3 flex-1">
                        <p class="font-semibold text-indigo-300">Como os filtros funcionam (ordem de aplicação)</p>

                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <span class="flex-none font-bold text-indigo-400">1. Status</span>
                                <span class="text-gray-600 dark:text-gray-300">
                                    Define o conjunto inicial de webhooks. Selecionar um ou mais status retorna a
                                    <strong>união</strong> deles (ex.: "Pago" + "Aprovado" = webhooks com qualquer um dos dois).
                                    As opções <em>Sucesso (todos)</em>, <em>Falha (todos)</em> e <em>Pendente / Em análise (todos)</em>
                                    são atalhos que expandem automaticamente para todos os status do grupo.
                                    Sem seleção, todos os webhooks da transação são considerados.
                                </span>
                            </div>

                            <div class="flex gap-2">
                                <span class="flex-none font-bold text-indigo-400">2. Tipo de evento</span>
                                <span class="text-gray-600 dark:text-gray-300">
                                    Aplicado <strong>dentro</strong> do resultado do filtro de Status.
                                    Busca pelo valor do campo <span class="font-black italic text-gray-900 dark:text-white tracking-wide">event</span> (ou <span class="font-black italic text-gray-900 dark:text-white tracking-wide">type</span> / <span class="font-black italic text-gray-900 dark:text-white tracking-wide">action</span>) no payload do webhook.
                                    Aceita parte do nome — ex.: <span class="font-black italic text-gray-900 dark:text-white tracking-wide">paid</span>
                                    encontra <span class="font-black italic text-gray-900 dark:text-white tracking-wide">order.paid</span>, mas
                                    <strong>não</strong> casa com campos como <span class="font-black italic text-gray-900 dark:text-white tracking-wide">paid_at</span>.
                                </span>
                            </div>

                            <div class="flex gap-2">
                                <span class="flex-none font-bold text-indigo-400">3. Ordenação</span>
                                <span class="text-gray-600 dark:text-gray-300">
                                    Aplicada sobre o conjunto final filtrado. Ordena por <span class="font-black italic text-gray-900 dark:text-white tracking-wide">created_at</span> + <span class="font-black italic text-gray-900 dark:text-white tracking-wide">id</span> para desempate.
                                </span>
                            </div>
                        </div>

                        <div class="border-t border-indigo-500/30 pt-2 text-gray-400">
                            <strong class="text-gray-600 dark:text-gray-300">Indicadores visuais:</strong>
                            <span class="ml-1 inline-flex items-center gap-2">
                                <span class="inline-block h-2.5 w-2.5 rounded-full bg-green-500"></span> Sucesso
                                <span class="inline-block h-2.5 w-2.5 rounded-full bg-yellow-500"></span> Pendente / Em análise
                                <span class="inline-block h-2.5 w-2.5 rounded-full bg-red-500"></span> Falha
                            </span>
                            — derivados do <span class="font-black italic text-gray-900 dark:text-white tracking-wide">status</span> do registro e do evento no payload quando o status for inconclusivo.
                        </div>
                    </div>

                    <button
                        type="button"
                        class="flex-none text-gray-400 hover:text-white"
                        @click="showHelp = false"
                    >
                        <XMarkIcon class="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div class="flex flex-wrap items-end gap-3 px-6 py-3 border-b border-black/10 dark:border-gray-600">
                <div class="flex flex-col">
                    <label class="text-xs text-gray-600 dark:text-gray-300 mb-1">Ordenação</label>
                    <select
                        v-model="sort"
                        class="text-input"
                        @change="reload"
                    >
                        <option value="asc">Mais antigo primeiro</option>
                        <option value="desc">Mais recente primeiro</option>
                    </select>
                </div>

                <div class="flex flex-col min-w-[200px]">
                    <label class="text-xs text-gray-600 dark:text-gray-300 mb-1">Status</label>
                    <multiple-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        placeholder="Todos"
                    />
                </div>

                <div class="flex flex-col flex-1 min-w-[180px]">
                    <label class="text-xs text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1">
                        Tipo de evento
                        <QuestionMarkCircleIcon
                            v-tooltip="'O nome do evento que o webhook representa, como \'order.paid\' ou \'subscription.canceled\'. A busca procura esse texto dentro do conteúdo (payload) do webhook, então pode usar partes do nome (ex.: \'paid\', \'refund\').'"
                            class="h-4 w-4 text-gray-400 hover:text-gray-200 cursor-help"
                        />
                    </label>
                    <input
                        v-model="eventFilter"
                        type="text"
                        class="text-input"
                        placeholder="Ex.: order.paid"
                    />
                </div>

                <button
                    type="button"
                    class="btn-primary"
                    :disabled="loading"
                    @click="reload"
                >
                    Aplicar filtros
                </button>

                <button
                    v-if="hasActiveFilters"
                    type="button"
                    class="btn-outline-primary px-5 py-2"
                    :disabled="loading"
                    @click="clearFilters"
                >
                    Limpar filtros
                </button>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-4">
                <ticto-loading v-if="loading && webhooks.length === 0" />

                <div
                    v-else-if="webhooks.length === 0"
                    class="text-center py-10 text-gray-600 dark:text-gray-300"
                >
                    Nenhum webhook encontrado para esta transação.
                </div>

                <ol v-else class="relative border-l border-black/10 dark:border-gray-600 ml-3">
                    <li
                        v-for="webhook in webhooks"
                        :key="webhook.id"
                        class="mb-6 ml-6"
                    >
                        <span
                            class="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-gray-700"
                            :class="indicatorBg(webhook)"
                        >
                            <XMarkIcon v-if="isFailure(webhook)" class="h-3 w-3 text-gray-900 dark:text-white" />
                            <ClockIcon v-else-if="isProcessing(webhook)" class="h-3 w-3 text-gray-900 dark:text-white" />
                            <CheckIcon v-else class="h-3 w-3 text-gray-900 dark:text-white" />
                        </span>

                        <div class="rounded-lg bg-gray-800 p-3">
                            <div class="flex flex-wrap items-center gap-2">
                                <span
                                    class="rounded px-2 py-0.5 text-xs font-medium text-gray-900 dark:text-white"
                                    :class="indicatorBg(webhook)"
                                >
                                    {{ webhook.status_label || webhook.status || '—' }}
                                </span>
                                <span class="ml-auto flex items-center text-xs text-gray-600 dark:text-gray-300">
                                    <ClockIcon class="mr-1 h-4 w-4" />
                                    {{ webhook.created_at || '—' }}
                                </span>
                            </div>

                            <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-900 dark:text-white">
                                <div>
                                    <span class="text-gray-400">Gateway:</span>
                                    {{ webhook.gateway?.name || 'N/A' }}
                                </div>
                                <div>
                                    <span class="text-gray-400">Criado em:</span>
                                    {{ webhook.created_at || '—' }}
                                </div>
                                <div>
                                    <span class="text-gray-400">Atualizado em:</span>
                                    {{ webhook.updated_at || '—' }}
                                </div>
                            </div>

                            <button
                                type="button"
                                class="btn-primary mt-2"
                                @click="toggle(webhook.id)"
                            >
                                {{ expanded === webhook.id ? 'Ocultar payload' : 'Ver payload' }}
                            </button>

                            <div v-if="expanded === webhook.id" class="mt-3">
                                <pre
                                    class="max-h-72 overflow-auto rounded bg-gray-900 p-3 text-xs text-white whitespace-pre-wrap break-all"
                                >{{ formatPayload(webhook.payload) }}</pre>
                            </div>
                        </div>
                    </li>
                </ol>

                <div v-if="loading && webhooks.length > 0" class="py-3">
                    <ticto-loading />
                </div>

                <div v-if="hasMore && !loading" class="mt-3 flex justify-center">
                    <button class="btn-primary" @click="loadMore">
                        Carregar mais
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import { CheckIcon, ClockIcon, QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import Modal from "../../../components/layouts/Modal.vue";
import MultipleSelect from "../../../components/forms/MultipleSelect.vue";
import TictoLoading from "../../../components/layouts/TictoLoading.vue";

export default {
    components: { Modal, MultipleSelect, TictoLoading, CheckIcon, ClockIcon, QuestionMarkCircleIcon, XMarkIcon },
    props: {
        transaction: { type: Object, required: true },
        show: { type: Boolean, default: false },
    },
    emits: ["closeModal"],
    data() {
        return {
            loading: false,
            showHelp: false,
            webhooks: [],
            currentPage: 0,
            lastPage: 1,
            sort: "asc",
            statusFilter: [],
            eventFilter: "",
            expanded: null,
            statusOptions: [
                // Atalhos semânticos — selecionam o grupo inteiro via backend
                { value: "success", label: "Sucesso (todos)" },
                { value: "processing", label: "Pendente / Em análise (todos)" },
                { value: "error", label: "Falha (todos)" },
                // Sucesso — individuais
                { value: "paid", label: "Pago" },
                { value: "approved", label: "Aprovado" },
                { value: "authorized", label: "Autorizado" },
                { value: "active", label: "Ativo" },
                { value: "completed", label: "Concluído" },
                { value: "created", label: "Criado" },
                { value: "sent", label: "Enviado" },
                { value: "delivered", label: "Entregue" },
                // Pendente / Em análise — individuais
                { value: "disputed", label: "Em disputa" },
                { value: "refund_requested", label: "Reembolso solicitado" },
                { value: "waiting_payment", label: "Aguardando pagamento" },
                { value: "mediation", label: "Em mediação" },
                { value: "under_review", label: "Em análise" },
                { value: "pending", label: "Pendente" },
                { value: "waiting", label: "Aguardando" },
                // Falha — individuais
                { value: "rejected", label: "Rejeitado" },
                { value: "failed", label: "Falha" },
                { value: "refused", label: "Recusado" },
                { value: "denied", label: "Negado" },
                { value: "cancelled", label: "Cancelado" },
                { value: "expired", label: "Expirado" },
                { value: "chargeback", label: "Chargeback" },
                { value: "chargeback_requested", label: "Chargeback solicitado" },
                { value: "chargeback_settled", label: "Chargeback encerrado" },
                { value: "chargeback_refunded", label: "Reembolso por chargeback" },
                { value: "refunded", label: "Estornado" },
                { value: "fraud_refunded", label: "Estorno por fraude" },
                { value: "void", label: "Anulado" },
                { value: "reversed", label: "Revertido" },
            ],
        };
    },
    computed: {
        hasMore() {
            return this.currentPage < this.lastPage;
        },
        hasActiveFilters() {
            return this.statusFilter.length > 0 || this.eventFilter !== "";
        },
    },
    watch: {
        show(value) {
            if (value && this.webhooks.length === 0) {
                this.reload();
            }
        },
    },
    mounted() {
        if (this.show) {
            this.reload();
        }
    },
    methods: {
        isFailure(webhook) {
            return (webhook.outcome || "") === "failure";
        },
        isProcessing(webhook) {
            return (webhook.outcome || "") === "processing";
        },
        indicatorBg(webhook) {
            if (this.isFailure(webhook)) {
                return "bg-red-500";
            }

            if (this.isProcessing(webhook)) {
                return "bg-yellow-500";
            }

            return "bg-green-500";
        },
        parsePayload(payload) {
            if (!payload) {
                return null;
            }

            if (typeof payload === "object") {
                return payload;
            }

            try {
                return JSON.parse(payload);
            } catch (e) {
                return null;
            }
        },
        formatPayload(payload) {
            const parsed = this.parsePayload(payload);

            if (parsed) {
                return JSON.stringify(parsed, null, 2);
            }

            return payload || "—";
        },
        clearFilters() {
            this.statusFilter = [];
            this.eventFilter = "";
            this.reload();
        },
        reload() {
            this.webhooks = [];
            this.currentPage = 0;
            this.lastPage = 1;
            this.expanded = null;
            this.fetch();
        },
        loadMore() {
            this.fetch();
        },
        fetch() {
            this.loading = true;

            const params = {
                sort: this.sort,
                page: this.currentPage + 1,
            };

            if (this.statusFilter.length > 0) {
                params.status = this.statusFilter;
            }

            if (this.eventFilter) {
                params.event = this.eventFilter;
            }

            axios
                .get(
                    `/api/webhook/transaction/${this.transaction.id}/history`,
                    { params },
                )
                .then(({ data }) => {
                    this.webhooks = [...this.webhooks, ...(data.data || [])];
                    this.currentPage = data.current_page || 1;
                    this.lastPage = data.last_page || 1;
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text:
                                response?.data?.message ||
                                "Falha ao carregar histórico de webhooks.",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggle(id) {
            this.expanded = this.expanded === id ? null : id;
        },
    },
};
</script>
